import { useEffect, useState } from "react";
import { useLocation } from "react-router";
import { api } from "../../lib/api";

/** Conversations sends this after it marks a thread read, so the count drops at once. */
export const UNREAD_CHANGED = "fr:unread-changed";

const FRESH_MS = 30_000;
let cached: { count: number; at: number } | null = null;
let inflight: Promise<number> | null = null;

function fetchCount(): Promise<number> {
  inflight ??= api.talk
    .rail()
    .then((res) => {
      cached = { count: res.directUnread, at: Date.now() };
      return cached.count;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/**
 * How many Direct threads wait for a reply, for the badge on the Conversations
 * tab. Read when the app opens, on a move to another page at most every half
 * minute, when the app comes back to the front, and whenever Conversations says
 * it changed. Nothing is asked for while no badge is on screen (`enabled`).
 */
export function useUnreadCount(enabled: boolean): number {
  const { pathname } = useLocation();
  const [count, setCount] = useState(cached?.count ?? 0);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const load = (force: boolean) => {
      if (!force && cached && Date.now() - cached.at < FRESH_MS) {
        setCount(cached.count);
        return;
      }
      fetchCount()
        .then((n) => {
          if (alive) setCount(n);
        })
        .catch(() => {});
    };
    load(false);
    const onChange = () => load(true);
    const onVisible = () => {
      if (document.visibilityState === "visible") load(true);
    };
    window.addEventListener(UNREAD_CHANGED, onChange);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      alive = false;
      window.removeEventListener(UNREAD_CHANGED, onChange);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [pathname, enabled]);

  return count;
}
